// src/components/RhinoVOIPRecentCalls.jsx
import React, { useState, useEffect } from 'react';
import { useGetRecentCallsQuery } from '../services/VoipService';
import useIsMobile from '../hooks/useIsMobile';
import '../assets/styles/rhinovoip-recent-calls.less';
import { useVoip } from '../voip/VoipProvider';
import incomingCallIcon from '../assets/images/voip/incoming-call-icon.svg';
import outgoingCallIcon from '../assets/images/voip/outgoing-call-icon.svg';
import ringGroupIcon from '../assets/images/voip/ring-group-icon.svg';

// ------------------ Component ------------------

const RhinoVOIPRecentCalls = () => {
  const { myExtension, call: placeCall, callPhase } = useVoip();
  const [includeRinggroups, setIncludeRinggroups] = useState(true);
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useGetRecentCallsQuery(includeRinggroups);

  useEffect(() => {
    let t;

    if (callPhase === 'ended') {
      t = setTimeout(() => {
        refetch();
      }, 500);
    }

    return () => {
      if (t) clearTimeout(t);
    };
  }, [callPhase, refetch]);

  const [confirmCall, setConfirmCall] = useState(null);
  const isMobile = useIsMobile(768);
  const VOICEMAIL_LABEL = 'My Voicemail';

  if (isLoading) return <div>Loading call logs...</div>;
  if (error) return <div>Error loading call logs</div>;

  const calls = Array.isArray(data?.objects) ? data.objects : [];

  function extractCallTarget(call) {
    const type = classifyDirection(call);

    if (type === 'Outgoing') {
      return call.destination_number;
    }

    return call.caller_id_number;
  }

  function openCallConfirm(call) {
    const target = extractCallTarget(call, myExtension);

    let label = '';

    if (target === '*97') {
      label = VOICEMAIL_LABEL;
    } else {
      label = call.dest_name ||
        call.ring_group_name ||
        target;
    }

    setConfirmCall({ targetNumber: target, label });
  }

  function handleMobileClick(call) {
    const target = extractCallTarget(call);
    if (!target) return;

    try {
      placeCall(target);
    } catch (err) {
      console.error('Mobile call start failed:', err);
    }
  }

  function formatDate(iso) {
    if (!iso) return '-';

    const d = new Date(iso);
    return `${d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })} ${d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
    })}`;
  }

  function formatPhone(value) {
    if (!value) return value;

    // Extract digits ONLY
    const digits = value.replace(/\D/g, '');

    // 10-digit US domestic number
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    // 11-digit with leading 1: 1 + NPA NXX XXXX
    if (digits.length === 11 && digits.startsWith('1')) {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }

    // Otherwise return original text (extensions, names, etc.)
    return value;
  }

  function formatDuration(sec) {
    if (!sec || sec === 0) return '0s';
    if (sec < 60) return `${sec}s`;
    return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  }

  function displayWithRingGroupIcon(label, number = null, showIcon = true) {
    const text = number ? `${label} (${number})` : label;

    if (!showIcon) return text;

    return (
      <span className="ringgroup-label">
        <img src={ringGroupIcon} alt="Ring Group" className="ringgroup-icon" /> {text}
      </span>
    );
  }

  // ------------------ Direction Classification ------------------

  function classifyDirection(call) {
    const dest = call.destination_number;
    const cid = call.caller_id_number;
    const dir = call.direction;

    // --- Voicemail Dial (OUTGOING) ---
    // If I called *97 → Outgoing
    if (cid === myExtension && dest === '*97') return 'Outgoing';

    // --- External outbound ---
    if (dir === 'outbound') return 'Outgoing';

    // --- External inbound ---
    if (dir === 'inbound') return 'Incoming';

    // --- Internal calls ----
    if (dir === 'local') {
      if (cid === myExtension) return 'Outgoing';
      return 'Incoming';
    }

    return dir;
  }

  // ------------------ UI Icons ------------------
  function directionIcon(type) {
    switch (type) {
      case 'Outgoing':
      case 'Outbound External':
        return outgoingCallIcon;
      case 'Incoming':
      case 'Inbound External':
        return incomingCallIcon;
      default:
        return '';
    }
  }

  // ------------------ Caller / Receiver Logic ------------------

  function callFrom(call) {
    const num = call.caller_id_number;
    const name = call.caller_id_name || num || 'Unknown';

    // Me
    if (num === myExtension) return `Me (${num})`;

    if (name === num) {
      return formatPhone(num);
    } else {
      return formatPhone(`${name} (${num})`);
    }
  }

  function callTo(call) {
    const dest = call.destination_number;
    const destName = call.dest_name;
    const rgName = call.ring_group_name;
    const type = classifyDirection(call);

    // Voicemail
    if (dest === '*97') return `${VOICEMAIL_LABEL} (${dest})`;

    // To Me
    if (dest === myExtension) return `Me (${dest})`;

    // Ring Group
    if (rgName) return `${rgName} (${dest})`;

    // Named internal
    if (destName !== dest) {
      if (destName && type !== 'Outbound External') return `${destName} (${dest})`;
    }

    // Fallback number only
    return formatPhone(dest) || '-';
  }

  function displayParty(call) {
    const type = classifyDirection(call);

    // If outgoing → show the person you're calling
    if (type === 'Outgoing' || type === 'Outbound External') {
      return callTo(call);
    }

    // Otherwise incoming → show the caller
    return callFrom(call);
  }

  function displayCallTarget(call) {
    const rgName = call.ring_group_name;

    if (rgName) {
      return displayWithRingGroupIcon(rgName, call.ring_group_extension, true);
    }

    if (isMobile) {
      return '';
    }

    return 'Direct';
  }

  function formatStatus(str) {
    if (!str) return '-';

    return str
      .replace(/_/g, ' ')
      .trim()
      .toLowerCase()
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  function getCallConfirmDialogtext() {
    const { label, targetNumber } = confirmCall;

    if (label === targetNumber) {
      return `Call ${formatPhone(targetNumber)}?`;
    } else {
      return `Call ${label} at ${formatPhone(targetNumber)}?`;
    }
  }

  return (
    <div className="cdr-container">

      {/* Checkbox */}
      <label
        style={{
          display: 'flex',
          gap: '6px',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <input
          type="checkbox"
          checked={includeRinggroups}
          onChange={(e) => setIncludeRinggroups(e.target.checked)}
        />
        Include Ring Group Calls
      </label>

      {isMobile ? (
        // -------- MOBILE VIEW --------
        <div className="cdr-mobile-list">
          {calls.map((call) => {
            const type = classifyDirection(call);
            const icon = directionIcon(type);

            return (
              <div key={call.xml_cdr_uuid} className="cdr-mobile-card" onClick={() => handleMobileClick(call)}>
                <div className="cdr-header-row">
                  <span className="cdr-name"><img src={icon} alt="direction" className="direction-icon" />&nbsp; &nbsp;{displayParty(call)} <br /> {displayCallTarget(call)}</span>
                  <span className="cdr-ts">{formatDate(call.start_stamp)}</span>
                </div>

                <div className="cdr-meta">
                  {` ${formatStatus(call.status) || 'n/a'} • ${formatDuration(call.duration)}`}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // -------- DESKTOP TABLE VIEW --------
        <div className="cdr-table-container">
          <table className="cdr-table">
            <thead>
              <tr>
                <th>Party</th>
                <th>Direct/Group</th>
                <th>Status</th>
                <th>Start</th>
                <th>Call Duration</th>
                <th> </th>
              </tr>
            </thead>

            <tbody>
              {calls.map((call) => {
                const type = classifyDirection(call);
                const icon = directionIcon(type);
                return (
                  <tr key={call.xml_cdr_uuid} className={call.status?.toLowerCase() === 'missed' ? 'cdr-row-missed' : ''}>
                    <td>
                      <img src={icon} alt="direction" className="direction-icon" title={type} />&nbsp;&nbsp;
                      {displayParty(call)}
                    </td>
                    <td>
                      {displayCallTarget(call)}
                    </td>
                    <td>{formatStatus(call.status)}</td>
                    <td>{formatDate(call.start_stamp)}</td>
                    <td>{formatDuration(call.duration)}</td>
                    {/* <td>{call.voicemail_message ? '📩' : ''}
                    <button type="button" className="cdr-call-btn" onClick={() => openCallConfirm(call)}>Call</button></td> */}
                    <td><button type="button" className="cdr-call-btn" onClick={() => openCallConfirm(call)}>Call</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {confirmCall && (
        <div className="cdr-modal-overlay">
          <div className="cdr-modal">
            <div className="cdr-modal-title">Confirm Call</div>
            <div className="cdr-modal-body">
              {getCallConfirmDialogtext(confirmCall)}
            </div>

            <div className="cdr-modal-actions">
              <button
                type="button"
                className="cdr-modal-cancel"
                onClick={() => setConfirmCall(null)}
              >
                Cancel
              </button>

              <button
                type="button"
                className="cdr-modal-confirm"
                onClick={() => {
                  placeCall(confirmCall.targetNumber);
                  setConfirmCall(null);
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RhinoVOIPRecentCalls;
